import { Card, CardBody, Skeleton, Button } from "@heroui/react";
import { useGetA1XBalance } from "../hooks/useGetAccountTokens";
import { useGetAccountInfo } from "../hooks/sdkDappHooks";
import makeBlockie from "ethereum-blockies-base64";
import { A1X_TOKEN } from "../config/sharedConfig";
import { useBuyA1XModal } from "../contexts/BuyA1XModalContext";
import useModalStore from "../store/useModalStore";
import { AssetImage } from "./AssetImage";

export const A1XBalance = () => {
  const { address } = useGetAccountInfo();
  const { data: a1xData, isLoading, error } = useGetA1XBalance();
  const { openModal } = useBuyA1XModal();
  const { setConnectWalletModalOpen } = useModalStore();

  // Generate blockies avatar from address
  const avatarUrl = address ? makeBlockie(address) : null;

  const handleBuyClick = () => {
    if (!address) {
      setConnectWalletModalOpen(true);
    } else {
      openModal();
    }
  };

  // Format balance to show full number with 3 decimals
  const formatBalance = (balance: string | undefined) => {
    if (!balance) return "0";
    const num = parseFloat(balance);
    return num.toLocaleString('en-US', { 
      minimumFractionDigits: 3,
      maximumFractionDigits: 3 
    });
  };

  return (
    <Card className="col-span-full">
      <CardBody className="p-4 md:p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3 md:gap-4 w-full sm:w-auto">
            {/* Blockies Avatar */}
            <div className="w-14 h-14 md:w-16 lg:w-20 md:h-16 lg:h-20 rounded-full overflow-hidden flex-shrink-0">
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt="User Avatar"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-gray-600 to-gray-700 rounded-full" />
              )}
            </div>

            {/* Balance Content */}
            <div className="flex-1 min-w-0">
              <p className="text-gray-400 text-sm md:text-base mb-1">
                {address ? "Your $A1X Balance:" : "Connect to view balance"}
              </p>
              {!address ? (
                <p className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-500">--</p>
              ) : isLoading ? (
                <Skeleton className="h-8 md:h-10 lg:h-12 w-32 md:w-48 lg:w-64 rounded-lg" />
              ) : error ? (
                <p className="text-red-400 text-sm">Error loading balance</p>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="text-2xl md:text-3xl lg:text-4xl font-bold text-white truncate">
                    {formatBalance(a1xData?.formattedBalance)}
                  </span>
                  <AssetImage identifier={A1X_TOKEN} className="w-6 h-6 md:w-8 md:h-8 lg:w-10 lg:h-10 flex-shrink-0" />
                </div>
              )}
            </div>
          </div>

          {/* Buy A1X Button */}
          <Button
            color="primary"
            onPress={handleBuyClick}
            size="md"
            className="font-bold w-full sm:w-auto h-12 md:h-12 px-4 md:px-5 flex items-center justify-center gap-2"
          >
            <span className="hidden sm:inline">{address ? "Buy A1X" : "Connect to Buy"}</span>
            <span className="sm:hidden">{address ? "Buy" : "Connect"}</span>
            <AssetImage identifier={A1X_TOKEN} className="w-5 h-5 md:w-6 md:h-6" />
          </Button>
        </div>
      </CardBody>
    </Card>
  );
};
